import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  DARK_QUERY,
  STORAGE_KEY,
  applyThemeClass,
  hydrateMode,
  readSystemPreference,
  resolveEffective,
  type EffectiveTheme,
  type ThemeMode,
} from '@/lib/themeBootstrap'

/**
 * Theme store — owns the user's light/dark/system preference and exposes the
 * *effective* theme that components / Chart.js / Leaflet should react to.
 *
 * Design choices (see docs/ux-redesign/PLAN.md §1 D-02..D-04):
 *
 *  - Mode is one of `'light' | 'dark' | 'system'`. `'system'` defers to
 *    `prefers-color-scheme` and live-updates when the OS toggles.
 *  - Effective theme is computed from mode + systemPreference and applied to
 *    `<html>` via the `.dark` class — that's the single hook downstream CSS
 *    (style.css `.dark { ... }`) and components watch.
 *  - Persisted in localStorage as a single key. Setup Store pattern with
 *    plain refs/computed/watchers — matches the rest of the codebase
 *    (chartRange, indicatorFilter) which roll their own persistence rather
 *    than pull in `pinia-plugin-persistedstate` for a single field.
 *  - The initial `<html class="dark">` toggle happens in `bootstrapTheme()`
 *    (main.ts) BEFORE Vue mounts — anti-FOUC. `init()` here only wires the
 *    runtime matchMedia listener so OS toggles propagate while the app runs.
 *
 * Storage key, query string, and DOM helpers live in `@/lib/themeBootstrap`
 * (single source of truth shared with the pre-mount bootstrap).
 */

// Re-export types from the bootstrap helper for ergonomic imports.
export type { EffectiveTheme, ThemeMode } from '@/lib/themeBootstrap'

export const useThemeStore = defineStore('theme', () => {
  // state
  const mode = ref<ThemeMode>(hydrateMode())
  const systemPreference = ref<EffectiveTheme>(readSystemPreference())

  // getters
  const effectiveTheme = computed<EffectiveTheme>(() =>
    resolveEffective(mode.value, systemPreference.value),
  )

  // internals — kept here so `init()` can detach the listener if ever
  // re-invoked (defensive; in practice it runs once for the app lifetime).
  let mediaQuery: MediaQueryList | null = null
  let mediaListener: ((e: MediaQueryListEvent) => void) | null = null
  let initialized = false

  /**
   * Attach the OS-pref matchMedia listener so `'system'` mode live-updates
   * when the OS toggles. Idempotent — repeated calls are no-ops after the
   * first. Note: the initial `<html class="dark">` toggle is performed by
   * `bootstrapTheme()` in main.ts BEFORE Vue mounts (anti-FOUC); this
   * function only handles runtime reactivity. The DOM is also re-synced
   * here as a defensive double-check in case the store loaded after some
   * other code mutated the class.
   */
  function init(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      // Still mark initialized so SSR/test runs don't keep retrying.
      initialized = true
      return
    }
    if (initialized) return

    // Defensive re-apply — bootstrapTheme() already did this pre-mount, but
    // re-syncing keeps the DOM consistent if anything mutated the class.
    applyThemeClass(effectiveTheme.value)

    mediaQuery = window.matchMedia(DARK_QUERY)
    mediaListener = (e: MediaQueryListEvent) => {
      systemPreference.value = e.matches ? 'dark' : 'light'
    }
    // `addEventListener` is the modern API. The older Safari (<14) fallback
    // (`addListener`) isn't needed — we target evergreen Chrome/Firefox/Safari
    // per PLAN §7. If support is ever broadened, add a guarded fallback.
    mediaQuery.addEventListener('change', mediaListener)

    // Flip the flag LAST so a thrown error above leaves the store
    // re-initializable. Prevents a half-initialized state being stuck.
    initialized = true
  }

  /** Cycle: light → dark → system → light. Wired to ThemeToggle. */
  function cycle(): void {
    const next: Record<ThemeMode, ThemeMode> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    }
    mode.value = next[mode.value]
  }

  /** Set mode explicitly — exposed for programmatic toggles (tests, future
   *  settings page). Falls through to the same watcher → DOM sync path. */
  function setMode(next: ThemeMode): void {
    mode.value = next
  }

  // Sync DOM whenever the effective theme changes (mode flip OR OS pref flip).
  watch(effectiveTheme, (t) => {
    applyThemeClass(t)
  })

  // Persist mode (the user intent, not the resolved value).
  watch(mode, (m) => {
    try {
      localStorage.setItem(STORAGE_KEY, m)
    } catch {
      // localStorage unavailable (private mode / quota) — keep in-memory
      // state and silently skip persistence.
    }
  })

  return {
    // state
    mode,
    systemPreference,
    // getters
    effectiveTheme,
    // actions
    init,
    cycle,
    setMode,
  }
})
